import { useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import PageMasthead from "../v2/PageMasthead";
import { formatDate } from "../../utils/formatters";

export type PricingNotice = {
  effectiveDate: string;
  rateCardUrl: string;
  calculationGuideUrl: string;
  rateCardViewUrl: string;
  calculationGuideViewUrl: string;
};

type Props = {
  notice: PricingNotice;
  /** When the user marked the notice read; null while it is still unread. */
  readAt: string | Date | null;
  pending: boolean;
  error: boolean;
  onMarkRead: () => void;
  onClose: () => void;
};

export const toIso = (value: string | Date) =>
  value instanceof Date ? value.toISOString() : value;

/**
 * The pricing notice, opened from the Messages menu. It is a message to
 * read, not a gate: Escape and the close button always work, and a single
 * "Mark as read" writes the read receipt (Ari, 2026-09-16).
 */
export function PricingNoticeDialog({
  notice,
  readAt,
  pending,
  error,
  onMarkRead,
  onClose,
}: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [document, setDocument] = useState<"rates" | "guide">("rates");
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);

  return (
    <dialog
      ref={dialog}
      aria-label="Updated fulfillment pricing"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-4xl overflow-y-auto rounded-xl border border-line bg-white p-6 text-ink shadow-xl backdrop:bg-black/40 sm:p-8"
    >
      <div className="flex items-start justify-between gap-4">
        <PageMasthead
          title="Updated fulfillment pricing"
          meta={`Effective ${notice.effectiveDate}`}
        />
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="rounded-lg p-2 text-ink-soft hover:bg-gray-100 hover:text-ink"
        >
          <XIcon size={18} />
        </button>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-ink-soft">
        Pay-As-You-Sell stays unchanged. Handling is priced per item by
        category; shipping is priced per order by packed weight. Review the
        updated rates below.
      </p>
      <div className="mt-5 flex flex-wrap gap-2" aria-label="Pricing documents">
        <button
          type="button"
          aria-pressed={document === "rates"}
          onClick={() => setDocument("rates")}
          className={`rounded-lg px-3 py-2 text-sm font-semibold ${document === "rates" ? "bg-brand-navy text-white" : "bg-gray-100 text-ink-soft"}`}
        >
          Rate card
        </button>
        <button
          type="button"
          aria-pressed={document === "guide"}
          onClick={() => setDocument("guide")}
          className={`rounded-lg px-3 py-2 text-sm font-semibold ${document === "guide" ? "bg-brand-navy text-white" : "bg-gray-100 text-ink-soft"}`}
        >
          How to calculate your costs
        </button>
      </div>
      <p className="mt-3 text-xs text-ink-soft">
        Scroll inside the document to read it in full.
      </p>
      <iframe
        key={document}
        title={
          document === "rates"
            ? "Global Bridge rate card"
            : "How to calculate fulfillment costs"
        }
        src={
          document === "rates"
            ? notice.rateCardViewUrl
            : notice.calculationGuideViewUrl
        }
        sandbox="allow-same-origin"
        className="mt-3 h-[38dvh] min-h-[220px] w-full rounded-lg border border-line bg-white"
      />
      <div className="my-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <a
          className="text-brand-navy underline underline-offset-4"
          href={notice.rateCardUrl}
          download
        >
          Download rate card (PDF)
        </a>
        <a
          className="text-brand-navy underline underline-offset-4"
          href={notice.calculationGuideUrl}
          download
        >
          Download calculation guide (PDF)
        </a>
      </div>
      {error && (
        <p role="alert" className="mt-4 text-sm text-red-700">
          We could not save this as read. Please try again.
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        {readAt ? (
          <p className="text-sm text-ink-soft">
            Read on {formatDate(toIso(readAt))}
          </p>
        ) : (
          <span />
        )}
        {readAt ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-brand-navy px-4 py-3 text-sm font-semibold text-white hover:bg-brand-navyHover"
          >
            Close
          </button>
        ) : (
          <button
            type="button"
            disabled={pending}
            onClick={onMarkRead}
            className="rounded-lg bg-brand-navy px-4 py-3 text-sm font-semibold text-white hover:bg-brand-navyHover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? "Saving…" : "Mark as read"}
          </button>
        )}
      </div>
    </dialog>
  );
}
